import type { PermissionRequest } from '@gestion-permisos/contracts';
import { type ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiProblem } from '../api/problem.js';
import { ApiErrorView } from '../components/api-error-view.js';
import { buttonClasses } from '../components/button-styles.js';
import { CARD_SURFACE } from '../components/card.js';
import { Modal, ModalFooter } from '../components/modal.js';
import { ProblemMessage } from '../components/problem-message.js';
import { useMe } from '../session/session.js';
import { useCancelRequest, useSubmitRequest } from './requests-api.js';

const PRIMARY_BUTTON = buttonClasses();
const SECONDARY_BUTTON = buttonClasses('outline');
const CONFLICT_NOTICE = 'Otra persona cambió la solicitud. Se cargaron los datos actuales: revísalos antes de volver a intentarlo.';

/**
 * Acciones del solicitante sobre su solicitud (W3): editar y enviar en `DRAFT`; cancelar en `DRAFT` o
 * `PENDING_APPROVAL`. Mostrarlas o no es solo presentación: la API decide (409, 412, 422 o 403 se muestran
 * tal cual). Sin aprobar, rechazar ni reasignar. Solo la usa el detalle propio: va en una tarjeta.
 *
 * La cancelación se confirma en una ventana (`Modal`), como en la Power App: «¿Cancelar esta solicitud?», el
 * comentario opcional y «Confirmar cancelación». Cerrarla (Volver, la X o Escape) no llama a la API. Editar y
 * Enviar siguen en la tarjeta. Mientras la ventana está abierta, sus avisos y errores se muestran dentro.
 */
export function RequestActions({ request }: { request: PermissionRequest }) {
  const me = useMe();
  const submit = useSubmitRequest(request.id);
  const cancel = useCancelRequest(request.id);
  const [cancelling, setCancelling] = useState(false);
  const [notice, setNotice] = useState<string | undefined>(undefined);

  const own = request.employeeId === me.id;
  const editable = request.status === 'DRAFT';
  const cancellable = request.status === 'DRAFT' || request.status === 'PENDING_APPROVAL';
  const error = submit.error ?? cancel.error;
  const pending = submit.isPending || cancel.isPending;
  if (!own || (!cancellable && error === null && notice === undefined)) return null;

  const onConflict = (failure: unknown) => setNotice(failure instanceof ApiProblem && failure.status === 412 ? CONFLICT_NOTICE : undefined);
  const forbidden = error instanceof ApiProblem && error.status === 403;
  const open = cancellable && cancelling;
  // Avisos y errores: dentro de la ventana mientras está abierta; si no, en la tarjeta (nunca en los dos).
  const feedback = (
    <>
      {notice ? <output className="block rounded-md border border-border bg-muted p-3 text-sm">{notice}</output> : null}
      {error !== null ? forbidden ? <ApiErrorView error={error} /> : <ProblemMessage error={error} /> : null}
    </>
  );

  return (
    <section aria-labelledby="acciones" className={`space-y-3 p-5 ${CARD_SURFACE}`}>
      <h2 id="acciones" className="text-base leading-none font-semibold tracking-tight">
        Acciones
      </h2>
      {open ? null : feedback}
      <div className="flex flex-wrap gap-2">
        {editable ? (
          <>
            <Link to={`/solicitudes/${request.id}/editar`} className={SECONDARY_BUTTON}>
              Editar
            </Link>
            <button
              type="button"
              disabled={pending}
              className={PRIMARY_BUTTON}
              onClick={() => {
                cancel.reset();
                setNotice(undefined);
                submit.mutate(request.version, { onSuccess: () => setNotice('Solicitud enviada para aprobación.'), onError: onConflict });
              }}
            >
              Enviar
            </button>
          </>
        ) : null}
        {cancellable ? (
          <button
            type="button"
            disabled={pending}
            className={SECONDARY_BUTTON}
            onClick={() => {
              setNotice(undefined);
              setCancelling(true);
            }}
          >
            Cancelar solicitud
          </button>
        ) : null}
      </div>
      {open ? (
        <Modal
          title="¿Cancelar esta solicitud?"
          description="El registro se conservará y su estado cambiará a Cancelada. Esta acción no se puede revertir."
          onClose={() => setCancelling(false)}
        >
          <CancelForm
            pending={pending}
            feedback={feedback}
            onBack={() => setCancelling(false)}
            onConfirm={(comment) => {
              submit.reset();
              setNotice(undefined);
              cancel.mutate(
                { version: request.version, comment },
                {
                  onSuccess: () => {
                    setCancelling(false);
                    setNotice('Solicitud cancelada.');
                  },
                  onError: onConflict,
                },
              );
            }}
          />
        </Modal>
      ) : null}
    </section>
  );
}

/** Comentario opcional de la cancelación; se monta con la ventana, así que empieza vacío cada vez. */
function CancelForm({
  pending,
  feedback,
  onBack,
  onConfirm,
}: {
  pending: boolean;
  feedback: ReactNode;
  onBack: () => void;
  onConfirm: (comment: string) => void;
}) {
  const [comment, setComment] = useState('');
  return (
    <form
      aria-label="Cancelar la solicitud"
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        onConfirm(comment);
      }}
    >
      {feedback}
      <div className="space-y-2">
        <label htmlFor="cancel-comment" className="text-sm font-medium">
          Comentario (opcional)
        </label>
        <textarea
          id="cancel-comment"
          rows={3}
          maxLength={2000}
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          className="block w-full rounded-md border border-input bg-card px-2 py-1.5"
        />
      </div>
      <ModalFooter>
        <button type="button" disabled={pending} className={SECONDARY_BUTTON} onClick={onBack}>
          Volver
        </button>
        <button type="submit" disabled={pending} className={PRIMARY_BUTTON}>
          Confirmar cancelación
        </button>
      </ModalFooter>
    </form>
  );
}
